import { useState, useEffect } from 'react';
import { RECURRENCE_TYPES, WEEKDAYS, defaultConfig, describeRecurrence } from '../utils/recurrence';

// ── Repeat icon ───────────────────────────────────────────────────────────
function RepeatIcon({ size = 13, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="17 1 21 5 17 9"/>
      <path d="M3 11V9a4 4 0 014-4h14"/>
      <polyline points="7 23 3 19 7 15"/>
      <path d="M21 13v2a4 4 0 01-4 4H3"/>
    </svg>
  );
}

// ── Weekday toggle pill ───────────────────────────────────────────────────
function DayPill({ day, selected, onToggle }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(day.value)}
      style={{
        width: 32, height: 32, borderRadius: '50%',
        border: `1.5px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
        background: selected ? 'var(--accent)' : 'transparent',
        color: selected ? 'white' : 'var(--text2)',
        fontSize: 11, fontWeight: 600, cursor: 'pointer',
        fontFamily: 'inherit', transition: 'all 0.15s',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >{day.label}</button>
  );
}

// ── Main RecurrencePicker ─────────────────────────────────────────────────
export default function RecurrencePicker({ value, onChange }) {
  // value = { type: 'weekly', config: { days: [1,3,5] } }
  const type   = value?.type   ?? 'none';
  const config = value?.config ?? null;

  const setType = (newType) => {
    onChange({ type: newType, config: newType === 'none' ? null : defaultConfig(newType) });
  };

  const setConfig = (patch) => {
    onChange({ type, config: { ...config, ...patch } });
  };

  const toggleDay = (day) => {
    const days    = config?.days ?? [1];
    const newDays = days.includes(day)
      ? days.filter(d => d !== day)
      : [...days, day].sort((a, b) => a - b);
    // Require at least one day
    if (newDays.length === 0) return;
    setConfig({ days: newDays });
  };

  return (
    <div>
      {/* Type selector */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 8, padding: '8px 12px',
        cursor: 'pointer',
      }}>
        <RepeatIcon color={type !== 'none' ? 'var(--accent)' : 'var(--text3)'} />
        <select
          value={type}
          onChange={e => setType(e.target.value)}
          style={{
            flex: 1, border: 'none', outline: 'none', background: 'transparent',
            fontSize: 13, color: type !== 'none' ? 'var(--text)' : 'var(--text2)',
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          {RECURRENCE_TYPES.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Config panel — shown when not 'none' */}
      {type !== 'none' && (
        <div style={{
          marginTop: 8, padding: '10px 12px',
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 8,
        }}>

          {/* Weekly: day picker */}
          {type === 'weekly' && (
            <div>
              <p style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, fontWeight: 500 }}>
                Repeat on
              </p>
              <div style={{ display: 'flex', gap: 5 }}>
                {WEEKDAYS.map(day => (
                  <DayPill
                    key={day.value}
                    day={day}
                    selected={(config?.days ?? [1]).includes(day.value)}
                    onToggle={toggleDay}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Monthly: day of month */}
          {type === 'monthly' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 13, color: 'var(--text2)' }}>Repeat on the</span>
              <input
                type="number"
                min={1} max={28}
                value={config?.day ?? 1}
                onChange={e => setConfig({ day: Math.max(1, Math.min(28, parseInt(e.target.value) || 1)) })}
                style={{
                  width: 52, textAlign: 'center',
                  border: '1px solid var(--border)', borderRadius: 6,
                  padding: '4px 6px', fontSize: 13,
                  background: 'var(--bg)', color: 'var(--text)',
                  fontFamily: 'inherit', outline: 'none',
                }}
              />
              <span style={{ fontSize: 13, color: 'var(--text2)' }}>of each month</span>
            </div>
          )}

          {/* Custom: every N days */}
          {type === 'custom' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 13, color: 'var(--text2)' }}>Every</span>
              <input
                type="number"
                min={1} max={365}
                value={config?.interval ?? 7}
                onChange={e => setConfig({ interval: Math.max(1, Math.min(365, parseInt(e.target.value) || 1)) })}
                style={{
                  width: 52, textAlign: 'center',
                  border: '1px solid var(--border)', borderRadius: 6,
                  padding: '4px 6px', fontSize: 13,
                  background: 'var(--bg)', color: 'var(--text)',
                  fontFamily: 'inherit', outline: 'none',
                }}
              />
              <span style={{ fontSize: 13, color: 'var(--text2)' }}>days</span>
            </div>
          )}

          {/* Preview label */}
          <p style={{
            marginTop: 8, fontSize: 11,
            color: 'var(--accent)', fontWeight: 500,
          }}>
            {describeRecurrence(type, config)}
          </p>
        </div>
      )}
    </div>
  );
}